import { useRef, useState } from "react";
import { ChevronDown, Plus, SlidersHorizontal, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { columnKindOf, type ColKind, type ColumnFilter } from "@/components/clicks/clicksGridEngine";
import { FILTER_FIELD_GROUPS, type FilterField } from "@/config/filterFields";
import { nextRowId, rowsFromState, type BuilderRow } from "@/lib/filter-builder";
import { useTextFilterModes } from "@/hooks/useFilterModes";
import { describeFilterValues, isTextMode } from "@/lib/filter-model";
import type { UrlActiveFilters, UrlFilterAnyMode } from "@/lib/url-active-filters";
import { ColumnFilterForm } from "./ColumnFilterForm";
import { ValueEditor } from "./ValueEditor";

/** A table column a condition can be put on. */
export type ColumnOption = { id: string; label: string; kind: ColKind };

type Groups = { title: string; fields: FilterField[] }[];

const selectClass =
  "h-8 rounded-md border border-border bg-transparent px-2 text-sm outline-none focus-visible:ring-1 focus-visible:ring-ring";

/**
 * "Filters · N": every filter as a row - field, condition, values - added with
 * "+ Add condition" and applied together (owner, 30.10.2026; variant 25). On
 * Apply the rows become the chips of the bar; Cancel, Esc or a click outside
 * throws the draft away. One row per field or column: a field taken by a row
 * is "in use" in the others (two rows quietly kept only the last). The button
 * is an outlined control like Bots - outlined manages, the filled "Add filter"
 * adds (owner, 03.11.2026; docs/clicks-filter-buttons-variants.html 2A). Open
 * is the bar's: "Add filter" opens it too (2D).
 */
export function FilterBuilder({ count, readCurrent, columns, fieldOf, extraGroups, onApply, open, onOpenChange }: {
  /** How many filters apply now, for the button. */
  count: number;
  /** The filters as they will be, read when the builder opens - not a render behind a write in flight. */
  readCurrent: () => { filters: UrlActiveFilters; columnFilters: Record<string, ColumnFilter> };
  columns: ColumnOption[];
  fieldOf: (key: string) => FilterField;
  extraGroups: Groups;
  onApply: (rows: BuilderRow[]) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [rows, setRows] = useState<BuilderRow[]>([]);
  // Opened by its button or from "Add filter": either way the rows start from
  // the filters as they are. Set during render (React's "adjust state on prop
  // change"), so the first frame already shows them.
  const [shownOpen, setShownOpen] = useState(open);
  if (open !== shownOpen) {
    setShownOpen(open);
    if (open) {
      const cur = readCurrent();
      setRows(rowsFromState(cur.filters, cur.columnFilters));
    }
  }
  const groups = [...FILTER_FIELD_GROUPS, ...extraGroups];
  const patch = (next: BuilderRow) => setRows((cur) => cur.map((r) => (r.id === next.id ? next : r)));
  const choiceOf = (r: BuilderRow) => (r.type === "field" ? `f:${r.key}` : r.type === "column" ? `c:${r.key}` : "");
  const setOpen = onOpenChange;

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          aria-label={count ? `Filters, ${count} applied. Edit all` : "Filters. Add several at once"}
          className="cursor-pointer gap-2"
        >
          <SlidersHorizontal className="h-4 w-4" aria-hidden />
          Filters
          {count ? (
            <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-accent px-1 text-[11px] font-semibold text-accent-foreground tabular-nums">
              {count}
            </span>
          ) : null}
          <ChevronDown className="h-4 w-4 text-muted-foreground" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[min(620px,calc(100vw-2rem))] space-y-2 p-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <p className="text-sm font-medium">Filters</p>
          <p className="text-[11px] text-muted-foreground">
            All conditions apply together; one row per field - for a range, use Between.
          </p>
        </div>

        <div className="space-y-2">
          {rows.length ? (
            rows.map((r) => (
              <Row
                key={r.id}
                row={r}
                groups={groups}
                columns={columns}
                fieldOf={fieldOf}
                taken={new Set(rows.filter((x) => x.id !== r.id).map(choiceOf).filter(Boolean))}
                onChange={patch}
                onRemove={() => setRows((cur) => cur.filter((x) => x.id !== r.id))}
              />
            ))
          ) : (
            <p className="py-2 text-xs text-muted-foreground">No filters yet.</p>
          )}
        </div>

        <button
          type="button"
          onClick={() => setRows((cur) => [...cur, { id: nextRowId(), type: "new" }])}
          className="inline-flex cursor-pointer items-center gap-1 text-xs text-primary hover:underline"
        >
          <Plus className="size-3" aria-hidden /> Add condition
        </button>

        <div className="flex justify-end gap-2 border-t border-border pt-2">
          <Button variant="ghost" size="sm" className="h-7" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            size="sm"
            className="h-7"
            onClick={() => {
              onApply(rows);
              setOpen(false);
            }}
          >
            Apply
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function Row({ row, groups, columns, fieldOf, taken, onChange, onRemove }: {
  row: BuilderRow;
  groups: Groups;
  columns: ColumnOption[];
  fieldOf: (key: string) => FilterField;
  /** Fields and columns other rows hold ("f:country", "c:total_revenue"). */
  taken: Set<string>;
  onChange: (next: BuilderRow) => void;
  onRemove: () => void;
}) {
  const value = row.type === "field" ? `f:${row.key}` : row.type === "column" ? `c:${row.key}` : "";
  const offered = new Set([...groups.flatMap((g) => g.fields.map((f) => `f:${f.key}`)), ...columns.map((c) => `c:${c.id}`)]);
  const kindOf = (id: string) => columns.find((c) => c.id === id)?.kind ?? columnKindOf(id);
  // A field picked with the mouse opens its values at once; with the arrow
  // keys every step would pick a field and the popover would take the focus.
  const byPointer = useRef(false);
  const [openValues, setOpenValues] = useState(false);
  const pick = (v: string) => {
    if (v.startsWith("f:")) {
      setOpenValues(byPointer.current);
      onChange({ id: row.id, type: "field", key: v.slice(2), mode: "include", values: [] });
    }
    if (v.startsWith("c:")) {
      const id = v.slice(2);
      onChange({ id: row.id, type: "column", key: id, draft: { op: kindOf(id) === "text" ? "contains" : "gt", a: "", b: "" } });
    }
  };
  const option = (choice: string, label: string) => (
    <option key={choice} value={choice} disabled={taken.has(choice)}>
      {taken.has(choice) ? `${label} · in use` : label}
    </option>
  );

  return (
    // Phone: field and × on the first line, the condition on the second;
    // from sm up, one line with × last.
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-border p-2 sm:flex-nowrap">
      <select
        aria-label="Field"
        value={value}
        onMouseDown={() => {
          byPointer.current = true;
        }}
        onKeyDown={() => {
          byPointer.current = false;
        }}
        onChange={(e) => pick(e.target.value)}
        className={`${selectClass} min-w-0 flex-1 sm:w-[170px] sm:flex-none`}
      >
        {row.type === "new" ? (
          <option value="" disabled>
            Choose a field…
          </option>
        ) : null}
        {/* A field from an old link, or another grouping's marks: still shown as itself. */}
        {value && !offered.has(value) && row.type !== "new" ? (
          <option value={value}>{row.type === "field" ? fieldOf(row.key).label : row.key}</option>
        ) : null}
        {groups.map((g) => (
          <optgroup key={g.title} label={g.title}>
            {g.fields.map((f) => option(`f:${f.key}`, f.label))}
          </optgroup>
        ))}
        {columns.length ? (
          <optgroup label="Table columns">{columns.map((c) => option(`c:${c.id}`, c.label))}</optgroup>
        ) : null}
      </select>

      <button
        type="button"
        onClick={onRemove}
        aria-label="Remove condition"
        className="inline-flex size-7 shrink-0 cursor-pointer items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground sm:order-last"
      >
        <X className="size-3.5" aria-hidden />
      </button>

      <div className="flex min-w-0 basis-full items-center gap-2 sm:flex-1 sm:basis-auto">
        {row.type === "field" ? (
          <FieldValues key={row.key} row={row} field={fieldOf(row.key)} initiallyOpen={openValues} onChange={onChange} />
        ) : null}
        {row.type === "column" ? (
          <ColumnFilterForm inline kind={kindOf(row.key)} draft={row.draft} onDraft={(draft) => onChange({ ...row, draft })} />
        ) : null}
        {row.type === "new" ? <span className="flex-1 text-xs text-muted-foreground">then its values</span> : null}
      </div>
    </div>
  );
}

function FieldValues({ row, field, initiallyOpen, onChange }: {
  row: Extract<BuilderRow, { type: "field" }>;
  field: FilterField;
  initiallyOpen: boolean;
  onChange: (next: BuilderRow) => void;
}) {
  const [open, setOpen] = useState(initiallyOpen && row.values.length === 0);
  // Text modes once the server knows them, never for a fixed list (marks).
  const textOn = useTextFilterModes() && !field.options;
  return (
    <>
      <select
        aria-label="Match"
        value={row.mode}
        onChange={(e) => {
          const mode = e.target.value as UrlFilterAnyMode;
          // Values and pieces of text are not the same thing: the other kind starts empty.
          onChange({ ...row, mode, values: isTextMode(mode) === isTextMode(row.mode) ? row.values : [] });
        }}
        className={`${selectClass} w-[120px] shrink-0`}
      >
        <option value="include">is</option>
        <option value="exclude">is not</option>
        {textOn || isTextMode(row.mode) ? (
          <>
            <option value="contains">contains</option>
            <option value="not_contains">doesn't contain</option>
          </>
        ) : null}
      </select>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="h-8 min-w-0 flex-1 cursor-pointer truncate rounded-md border border-border px-2 text-left text-sm hover:bg-muted"
            title={row.values.map((v) => (isTextMode(row.mode) ? `“${v}”` : (field.valueLabel?.(v) ?? v))).join(", ")}
          >
            {row.values.length ? (
              describeFilterValues({ mode: row.mode, values: row.values }, 3, field.valueLabel)
            ) : (
              <span className="text-muted-foreground">{isTextMode(row.mode) ? "Type text…" : "Choose values…"}</span>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-80 p-3">
          <ValueEditor
            field={field}
            draft={{ mode: row.mode, values: row.values }}
            onDraft={(d) => onChange({ ...row, mode: d.mode, values: d.values })}
            onDone={() => setOpen(false)}
          />
        </PopoverContent>
      </Popover>
    </>
  );
}
